import { z } from 'zod';
import { ExtensionDataClient } from '@wix/extensions-data-client';
import type { BackOfficeExtensionContainer } from '@wix/extensions-data-client';
import { getDashboardPluginExtensionsDir } from '@wix/cli-astro-definitions';
import {
  VALID_PATH_REGEX,
  folderExistsMessage,
  pathAvailableValidation,
} from '../../../../dev-center/components/validations/common.js';
import type { ExtensionDef } from '../../generate-schema/types.js';
import { defineField } from '../../generate-schema/definitions.js';
import { enrichWithDerivedFolder } from '../../utils/derive-folder.js';
import { generateDashboardPlugin } from './generate.js';

function buildSlotIdField(containers: BackOfficeExtensionContainer[]) {
  if (containers.length === 0) {
    return z.string().describe(
      defineField({
        title: 'Slot to extend',
        description:
          'Component ID of the back-office extension container to extend. Obtain from the Dev Center.',
      })
    );
  }

  const literals = containers.map((c) =>
    z.literal(c.component.componentId).describe(
      defineField({
        title:
          c.component.data.backOfficeExtensionContainer.slotData?.displayName ??
          c.component.name,
      })
    )
  );

  const field =
    literals.length === 1
      ? literals[0]!
      : z.union(
          literals as [
            z.ZodLiteral<string>,
            z.ZodLiteral<string>,
            ...Array<z.ZodLiteral<string>>,
          ]
        );

  return field.describe(
    defineField({
      title: 'Slot to extend',
      description:
        'Component ID of the back-office extension container to extend.',
    })
  );
}

export const dashboardPlugin: ExtensionDef = {
  title: 'Dashboard Plugin',
  description: 'A plugin that extends a back-office page with a custom widget',
  group: 'Dashboard',
  documentation:
    'https://dev.wix.com/docs/wix-cli/guides/extensions/dashboard-extensions/dashboard-plugins/add-dashboard-plugin-extensions',
  zodSchema: async (httpClient) => {
    const client = new ExtensionDataClient(httpClient);
    const containers = await client.getBackofficeExtensionWidgets();
    return z
      .object({
        extensionType: z.literal('DASHBOARD_PLUGIN'),
        extendsSlotId: buildSlotIdField(containers),
        title: z.string().describe(defineField({ title: 'Plugin title' })),
        folder: z
          .string()
          .regex(VALID_PATH_REGEX)
          .optional()
          .describe(
            defineField({
              title: 'Plugin folder',
              description:
                'Lowercase alphanumeric and hyphens only. Must be unique within src/dashboard/plugins/. Derived from the plugin title when omitted.',
            })
          ),
      })
      .transform((data) => ({
        ...data,
        slotsAppId: containers.find(
          (c) => c.component.componentId === data.extendsSlotId
        )?.appId,
      }));
  },
  enrich: (data) => enrichWithDerivedFolder(data, { sourceField: 'title' }),
  validate: async (projectFolder, data) => {
    const isAvailable = pathAvailableValidation(
      getDashboardPluginExtensionsDir(projectFolder)
    )(data.folder);
    return isAvailable
      ? null
      : folderExistsMessage('plugin', data.folder, 'title');
  },
  generate: async ({ projectFolder, data, httpClient }) => {
    const client = new ExtensionDataClient(httpClient);

    const sdkSlotData = data.slotsAppId
      ? await client
          .getSdkSlotData({
            slotId: data.extendsSlotId,
            appId: data.slotsAppId,
          })
          .catch(() => {})
      : undefined;

    return generateDashboardPlugin(
      projectFolder,
      data.extendsSlotId,
      data.folder,
      data.title,
      sdkSlotData?.importData
    );
  },
};
